import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

/**
 * Köttbutiken: "Passar bra med" på produktsidan (blocks/kb-passar-bra-med.liquid). Kryssrutorna hör till
 * produktformuläret och skickas med av product-form.js (kbMedTillagg). Här byts variant i väljaren, summan
 * för valda tillägg visas och kryssrutorna nollställs när produkten har lagts i varukorgen.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} summa
 *
 * @extends {Component<Refs>}
 */
class KbPassarBraMedComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#efterKop);
    this.uppdatera();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#efterKop);
  }

  /** @returns {HTMLInputElement[]} */
  get #kryssrutor() {
    return Array.from(this.querySelectorAll('input.kb-pbm__kryss'));
  }

  /** @param {Event} event */
  valjVariant(event) {
    const valjare = event.target;
    if (!(valjare instanceof HTMLSelectElement)) return;
    const rad = valjare.closest('.kb-pbm__rad');
    const vald = valjare.selectedOptions[0];
    const kryss = rad?.querySelector('input.kb-pbm__kryss');
    if (!rad || !vald || !(kryss instanceof HTMLInputElement)) return;

    kryss.value = valjare.value;
    kryss.dataset.pris = vald.dataset.pris ?? '0';
    const pris = rad.querySelector('[data-kb-pbm-pris]');
    if (pris) pris.textContent = vald.dataset.prisText ?? '';
    // Den som väljer vikt vill oftast ha tillägget
    kryss.checked = true;
    this.uppdatera();
  }

  uppdatera() {
    const { summa } = this.refs;
    if (!summa) return;
    const valda = this.#kryssrutor.filter((k) => k.checked);
    if (!valda.length) {
      summa.hidden = true;
      summa.textContent = '';
      return;
    }
    const total = valda.reduce((s, k) => s + (Number(k.dataset.pris) || 0), 0);
    const belopp = formatMoney(total, this.dataset.format || '{{amount}}', this.dataset.valuta || 'SEK');
    summa.textContent = (this.dataset.summaText || '')
      .replace('[antal]', String(valda.length))
      .replace('[belopp]', belopp);
    summa.hidden = false;
  }

  /** Tilläggen ligger nu i varukorgen: nollställ, så att de inte läggs till igen av misstag. */
  #efterKop = (/** @type {Event} */ event) => {
    const promise = /** @type {{ promise?: Promise<any> }} */ (event).promise;
    promise
      ?.then(({ detail } = {}) => {
        if (detail?.didError || detail?.source !== 'product-form-component') return;
        let andrad = false;
        for (const kryss of this.#kryssrutor) {
          if (kryss.checked) {
            kryss.checked = false;
            andrad = true;
          }
        }
        if (andrad) this.uppdatera();
      })
      .catch(() => {});
  };
}

if (!customElements.get('kb-passar-bra-med-component')) {
  customElements.define('kb-passar-bra-med-component', KbPassarBraMedComponent);
}
